import { useCallback, useMemo } from 'react';
import md5 from 'crypto-js/md5';
import { useInAppBrowser } from 'App/capacitor';
import { openWindow } from 'commons/utils/window';
import { useIsMobileDevice, useLoggedAdminLink } from 'domains/Auth/hooks';
import type { DesignType } from 'domains/Online/ContentPages/pages/types';

const INSTATHEME_PARAM = 'instatheme' as const;
const CUSTOM_PAGE_PREFIX = 'custom_page_' as const;
const CUSTOM_PAGE_SUFFIX = '_order_position' as const;
const BASE_THEME_EDITOR_PATH = '/themes/settings' as const;

export function useCustomPageThemeEditorLink() {
  const { getLoggedAdminLink, isLoading } = useLoggedAdminLink();
  const isMobileDevice = useIsMobileDevice();
  const openInAppBrowser = useInAppBrowser();

  const goToThemeEditor = useCallback(
    async (designType: DesignType, pageId: string) => {
      if (isLoading) {
        return;
      }

      const sectionParam = `${CUSTOM_PAGE_PREFIX}${pageId}${CUSTOM_PAGE_SUFFIX}`;
      const hash = md5(sectionParam).toString();
      const pathWithHash = `${BASE_THEME_EDITOR_PATH}/${
        designType === 'current' ? 'active' : 'draft'
      }#${INSTATHEME_PARAM}=${hash}`;
      const linkWithInstatheme = getLoggedAdminLink(pathWithHash);

      if (isMobileDevice) {
        await openInAppBrowser(linkWithInstatheme);
      } else {
        // Intentional: open in a new tab on desktop (do not delegate to useInAppBrowser).
        openWindow(linkWithInstatheme, true);
      }
    },
    [isLoading, isMobileDevice, getLoggedAdminLink, openInAppBrowser],
  );

  return useMemo(() => ({ goToThemeEditor } as const), [goToThemeEditor]);
}
